import React from 'react';
import { Modal } from 'react-bootstrap';
import {
  IconPlayerPause,
  IconPlayerPlay,
  IconX,
  IconAlertCircle,
  IconInfoCircle,
  IconCheck,
} from '@tabler/icons-react';
import PropTypes from 'prop-types';
import './PauseSubscriptionModal.css';

const PauseSubscriptionModal = ({
  isOpen,
  onClose,
  subscriptionActive,
  isProcessing,
  onConfirm,
  billingEndDate,
}) => {
  // Format billing end date if provided
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return null;
    }
  };

  const formattedEndDate = formatDate(billingEndDate);

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      centered
      size="md"
    >
      <Modal.Header
        className={`pause-subscription-modal-header ${
          subscriptionActive ? 'pause-state' : 'resume-state'
        }`}
      >
        <div>
          <Modal.Title as="h3">
            {subscriptionActive ? (
              <>
                <IconPlayerPause size={24} className="me-2" />
                Pause Subscription
              </>
            ) : (
              <>
                <IconPlayerPlay size={24} className="me-2" />
                Resume Subscription
              </>
            )}
          </Modal.Title>
          <p className="modal-subtitle">
            {subscriptionActive
              ? 'Temporarily suspend your subscription'
              : 'Reactivate your subscription'}
          </p>
        </div>
        <button
          className="modal-close-btn"
          onClick={onClose}
          disabled={isProcessing}
        >
          <IconX size={20} />
        </button>
      </Modal.Header>

      <Modal.Body className="pause-subscription-modal-body">
        {subscriptionActive ? (
          /* Pause State - Warning */
          <div className="warning-notice">
            <IconAlertCircle size={20} />
            <div className="notice-content">
              <p className="notice-message">
                Are you sure you want to pause the subscription? The account
                will remain active until the end of your current billing period,
                but it will not renew automatically.
              </p>
              {formattedEndDate && (
                <p className="notice-detail">
                  Your subscription will remain active until{' '}
                  <strong>{formattedEndDate}</strong>.
                </p>
              )}
            </div>
          </div>
        ) : (
          /* Resume State - Info */
          <div className="info-notice-resume">
            <IconInfoCircle size={20} />
            <div className="notice-content">
              <p className="notice-message">
                Are you sure you want to resume the subscription? The account
                will be reactivated and billing will continue according to your
                current plan.
              </p>
              <p className="notice-detail">
                Your next billing cycle will begin immediately.
              </p>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="pause-subscription-modal-footer">
        <button
          className="btn-cancel"
          disabled={isProcessing}
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          className={`btn-confirm ${
            subscriptionActive ? 'btn-pause' : 'btn-resume'
          }`}
          onClick={onConfirm}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              <span>Processing...</span>
            </>
          ) : subscriptionActive ? (
            <>
              <IconPlayerPause size={16} />
              Confirm Pause
            </>
          ) : (
            <>
              <IconCheck size={16} />
              Confirm Resume
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

PauseSubscriptionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  subscriptionActive: PropTypes.bool.isRequired,
  isProcessing: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  billingEndDate: PropTypes.string,
};

export default PauseSubscriptionModal;
